import classnames from 'classnames';
const EMPTY = Object.freeze({});

/**
 * the resource theme checks elements props and validates them against their theme, and will make some overrides based off props porvided from ui-builder
 */
class ResourceTheme {
  constructor({
    styles,
    supports,
    resources
  }) {
    this.styles = styles;
    this.supports = supports;
    this.rsrcs = resources || EMPTY;
    this.cache = {}; // for classname hierarchies
    this.propCache = this.createPropCache();
  }
  resolveVariant(componentType, variant) {
    const support = this.supports[componentType];
    if (variant == null || support == null || support.hierarchy == null) {
      return null;
    }
    const vs = variant.split('|');
    for (let n = 0; n < vs.length; n++) {
      if (support.hierarchy[vs[n]] !== undefined) {
        return vs[n];
      }
    }
    return null;
  }

  /**
   * Returns the classnames needed for the component's type and variant. 
   * @param {string} componentType the name of the component type. eg "Button"
   * @param {string} type the part of the component to get. eg "button"
   * @param {string} variant the variant name to requsest. eg "small"
   * @param {strings} rest other strings to add to the classname
   * @return {string} the classname
   */
  classes(componentType, type, variant, ...rest) {
    const style = this.styles[componentType];
    if (style) {
      const rv = this.resolveVariant(componentType, variant);
      const v = rv == null ? null : this.variantClassNames(componentType, rv);
      const c = classnames(this.usesDefault(componentType, rv) && style[type], v, ...rest);
      return c;
    }
    return classnames(...rest);
  }

  /**
   * Returns the classnames needed for the component's sub element. The variant
   * is not needed here because it will be in the css rule as a parent. 
   * @param {string} component the name of the component type. eg "Button"
   * @param {string} type the sub-component of the component to get. eg "button"
   * @param {strings} rest other strings to add to the classname
   * @return {string} the classname
   */
  subclasses(component, type, ...rest) {
    if (this.styles[component]) {
      return classnames(this.styles[component][type], ...rest);
    }
    return classnames(...rest);
  }

  /**
   * Returns any default props for the supplied Component and Variant.
   * If the third argument is an object, it will be overlaid on top of
   * the component's default props and the component's variant props.
   *
   * If the second argument is an object, it is considered to be the
   * React component's actual props object, and the variant is looked
   * up from it. This means from render, you can invoke:
   *   const { markClass } = theme.props( 'Checkbox', this.props )
   * @param {string} component the name of the component type. eg "Button"
   * @param {string|object} variant the variant name to requsest. eg "small"
   * @return {object} an object containing the default props. This will be a 
   * merger of the default variant's props and the specified variants props. 
   * This will always return an object
   */
  props(component, variant, userProps = EMPTY) {
    if (variant != null && typeof variant === 'object') {
      return this.props(component, variant.variant, variant);
    }
    const p = this.propCache[component]; // this.supports[component] && this.supports[component].props
    if (p) {
      const rv = this.resolveVariant(component, variant) || 'default';
      const v = p[rv] || EMPTY;
      // Remove undefined props
      const up = Object.keys(userProps).reduce((acc, key) => {
        if (userProps[key] !== undefined) acc[key] = userProps[key];
        return acc;
      }, {});
      return {
        ...v,
        ...up
      };
    }
    return userProps === EMPTY ? {} : userProps;
  }

  /**
   * Returns the resource set on the theme.
   * @param {String} type the type of resource
   * @param {String} name the name of the resource. If null, all of them are returned
   * @returns 
   */
  resources(type, name) {
    const t = this.rsrcs[type];
    if (name) {
      return t ? t[name] : null;
    }
    return t || EMPTY;
  }
  usesDefault(componentType, variant) {
    const ct = this.supports[componentType];
    const v = ct?.hierarchy?.[variant]?.[0];
    return v !== '__root__';
  }

  /**
   * Turns the variant's hierarchy into a hashified set of classnames.
   * @param {object} componentType the component type
   * @param {object} classes the jss classname lookup
   * @param {string} variant the component variant
   */
  variantClassNames(componentType, variant) {
    // get hierarchies for type
    const cache = this.cache[componentType] && this.cache[componentType][variant];
    if (cache) {
      return cache;
    }
    const ct = this.supports[componentType];
    if (ct && ct.hierarchy) {
      let r;
      const vh = ct.hierarchy[variant];
      if (vh) {
        // Get the compile/hashified name of the class name
        r = vh.map(cn => this.styles[componentType] && this.styles[componentType][cn]);
      } else {
        r = [];
      }
      this.cache[componentType] = this.cache[componentType] || {};
      this.cache[componentType][variant] = r;
      return r;
    }
    return null;
  }
  createPropCache() {
    const r = {};
    const s = this.supports;
    Object.keys(s).forEach(ct => {
      r[ct] = {};
      const props = s[ct].props || {};
      const hier = s[ct].hierarchy || {};
      const hks = Object.keys(hier);
      hks.unshift('default');
      hks.forEach(hk => {
        const isRoot = !this.usesDefault(ct, hk);
        const start = isRoot ? 1 : 0;
        const h = hier[hk];
        let prp = isRoot ? {} : props.default || {};
        if (h) {
          for (let n = start; n < h.length; n++) {
            // Strip <ct>- off of hk....consider more support objects
            const key = h[n].substring(h[n].indexOf('-') + 1);
            prp = {
              ...prp,
              ...props[key]
            };
          }
        }
        r[ct][hk] = prp;
      });
    });
    return r;
  }
}
export default ResourceTheme;